import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  useFieldArray,
  useController,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { Box, Typography, IconButton, Tooltip, Dialog, Button, TextField } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';
import { ReactComponent as MinusIcon } from '../../assets/minus.svg';
import { ReactComponent as ArrowIcon } from '../../assets/arrow.svg';
import { ReactComponent as QuestionIcon } from '../../assets/question.svg';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  blue60,
  gray40,
  gray60,
  gray80,
  gray100,
  orange50,
  white,
} from '../../../constants/colors';
import { regularSm, formLabelWrapper } from '../../../constants/location';
import CustomTextfield from '../form/CustomTextfield';
import CustomSelectField from '../form/CustomSelectField';
import {
  dataTypeOptions,
  displayWidthOptions,
  resolveDateOutputFormat,
} from '../../../constants/options';
import { displayWidth } from '../../../constants/enum';
import { HELP_FAQ_ITEM_SETTING_TIPS } from '../../../constants/externalUrls';
import {
  type ItemChoiceType,
  type ItemListType,
  type ItemListChildType,
} from '../../../types/common';
import { getOcrTargetItemCount, getMaxItemCount } from '../../../utils/pattern/validation';
import {
  validateFieldCodeFormat,
  sanitizeFieldCode,
  fillMissingFieldCodes,
  renameFieldCodeReferences,
} from '../../../utils/pattern/fieldCode';
import {
  isChoiceDataType,
  isCalculatableDataType,
  parentOnlyDataTypes,
  itemCharType,
  dateRangeUnit,
  type ItemCharType,
  type DateRangeUnit,
} from '../../../constants/itemValidation';
import { normalizeChoiceList, sanitizeChoices } from '../../../utils/pattern/choiceUtils';
import {
  pickRaw,
  pickNumber,
  pickString,
  pickBoolean,
  pickEnum,
  pickStringArray,
} from '../../../utils/pattern/jsonKeyPicker';
import { buildItemListExport } from '../../../utils/pattern/itemListJsonExport';
import { buildItemListDiff, type ItemDiffEntry } from '../../../utils/pattern/historyDiff';
import {
  collectImportMappingNotices,
  validateImportedItemIds,
  validateImportedSearchKeyItems,
} from '../../../utils/pattern/jsonImportValidation';
import { MasterWithMappingType } from '../../../types/master';
import { DictionaryWithMappingType } from '../../../types/dictionary';
import { buildCalculationEditContext } from '../../../utils/pattern/calculationEditContext';
import JsonImportDiffView from '../modal/JsonImportDiffView';
import ItemEditModal, {
  type CalculationEditContext,
  type EditItemData,
} from '../modal/ItemEditModal';

// 明細（複数行データ）の列で選べるデータ型のみに絞った選択肢（#3075）。
// 選択肢型はテーブルのセル内に収まらないため、子項目の行では出さない
const childDataTypeOptions = dataTypeOptions.filter(
  opt => !parentOnlyDataTypes.includes(opt.value),
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = any;

// 削除項目データの型
export type DeleteItemData = {
  id: string;
  itemName: string;
  isChild: boolean;
  parentIndex?: number;
  childIndex?: number;
};

// 詳細設定ボタンから渡されるデータ
export type EditItemRequest = {
  isChild: boolean;
  parentIndex: number;
  childIndex?: number;
};

interface ItemListManagerProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  setValue: UseFormSetValue<FormData>;
  trigger: UseFormTrigger<FormData>;
  watch: UseFormWatch<FormData>;
  disabled?: boolean;
  titleShow?: boolean;
  fieldName?: string; // デフォルト: 'itemList'
  validated?: boolean; // バリデーション済みフラグ（trueの場合、項目追加時にもバリデーションを実行）
  existingItemIds?: string[]; // 編集時の既存項目IDリスト（新規作成時は空配列またはundefined）
  onReRead?: () => Promise<void>; // AIで再読取ボタンのコールバック
  // 「リセットして手動入力」押下時のコールバック（#3147）
  // 指定時はリセットを実行せず呼び出し元に委譲する（編集時の確認モーダル用）
  onResetRequest?: () => void;
  onDeleteRequest?: (data: DeleteItemData) => void; // 既存項目削除時のモーダル表示コールバック
  onItemLimitExceeded?: () => void; // 項目数上限超過時のコールバック
  onItemInserted?: () => void; // 項目追加時のコールバック（バリデーション有効化などに使用）
  canImportJson?: boolean; // JSON取り込みボタンを表示するか（運営スタッフのみ）
  canCopyJson?: boolean; // JSONコピーボタンを表示するか（運営スタッフのみ）
  isEditMode?: boolean; // 既存パターンの編集中か（#3039）。JSONを初期表示し、項目IDの整合を検証する
  onShowMessage?: (message: string) => void; // メッセージ通知コールバック（コピー完了など）
  // このパターンに紐づくマスタ照合・辞書変換の設定（#3344）。
  // JSON取り込みで照合キーの項目が消される場合に差分プレビューへ進む前に弾く。
  patternMasters?: MasterWithMappingType[];
  patternDictionaries?: DictionaryWithMappingType[];
}

const styles = {
  formLabelWrapper,
  iconButton: {
    height: '40px',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
  },
  detailButton: {
    color: gray60,
    height: '32px',
    width: '32px',
    border: `1px solid ${gray60}`,
    borderRadius: '50%',
  },
  removeButton: {
    color: orange50,
    height: '32px',
    width: '32px',
    border: `1px solid ${orange50}`,
    borderRadius: '50%',
  },
  addButton: {
    color: orange50,
    height: '32px',
    width: '32px',
    border: `1px solid ${orange50}`,
    borderRadius: '50%',
  },
  dragButton: {
    color: gray40,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },
  convertButton: {
    color: gray60,
    height: '20px',
    width: '20px',
    padding: '2px',
  },
  addArrowIcon: {
    color: blue60,
    height: '32px',
    width: '32px',
    border: `1px solid ${blue60}`,
    borderRadius: '50%',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
  },
  childArrowIcon: {
    color: gray60,
    margin: '4px 8px',
    height: '20px',
    width: '20px',
  },
  label: {
    fontSize: '14px',
    display: 'flex',
  },
};

// ソート可能な子要素コンポーネント
interface SortableChildItemProps {
  id: string;
  parentIndex: number;
  childIndex: number;
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  fieldName: string;
  disabled: boolean;
  validated: boolean;
  watch: UseFormWatch<FormData>;
  onRemoveChild: (parentIndex: number, childIndex: number) => void;
  onInsertChild: (parentIndex: number, childIndex: number) => void;
  existingItemIds?: string[];
  onDeleteRequest?: (data: DeleteItemData) => void;
  onEditItem?: (data: EditItemRequest) => void;
}

const SortableChildItem: React.FC<SortableChildItemProps> = ({
  id,
  parentIndex,
  childIndex,
  control,
  errors,
  fieldName,
  disabled,
  validated,
  watch,
  onRemoveChild,
  onInsertChild,
  existingItemIds = [],
  onDeleteRequest,
  onEditItem,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  // 現在の子項目の状態を取得
  const parentData = watch(fieldName)?.[parentIndex];
  const currentChild = parentData?.children?.[childIndex];
  const isActive = currentChild?.is_active ?? true; // デフォルトはtrue

  // 選択肢型なのに選択肢が未設定か（#3075）。親項目と同じく詳細設定を促す
  const needsChoiceSetup =
    isChoiceDataType(currentChild?.type) && (currentChild?.choices?.length ?? 0) === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childErrors = (errors[fieldName] as any)?.[parentIndex]?.children?.[childIndex];
  const hasError = validated && Boolean(childErrors?.childValue);
  const errorMessage = childErrors?.childValue?.message;
  // 型フィールドのエラーをuseControllerで取得（errors.typeとFieldError.typeの名前衝突回避）
  const childTypeControllerName = `${fieldName}.${parentIndex}.children.${childIndex}.type`;
  const { fieldState: childTypeFieldState } = useController({
    control,
    name: childTypeControllerName,
  });
  const hasTypeError = validated && Boolean(childTypeFieldState.error);
  const typeErrorMessage = childTypeFieldState.error?.message;
  // 列幅フィールドのエラーを取得
  const childDisplayWidthName = `${fieldName}.${parentIndex}.children.${childIndex}.displayWidth`;
  const { fieldState: childDisplayWidthFieldState } = useController({
    control,
    name: childDisplayWidthName,
  });
  const hasDisplayWidthError = validated && Boolean(childDisplayWidthFieldState.error);
  const displayWidthErrorMessage = childDisplayWidthFieldState.error?.message;
  const hasAnyError = hasError || hasTypeError || hasDisplayWidthError;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1, // ドラッグ中のみ半透明（OFFでも行全体は薄くしない）
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ marginBottom: '10px', marginLeft: '36px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #CAD5E2',
          borderRadius: '8px',
          padding: '12px',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(239, 246, 255, 0.5)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            alignItems: 'center',
            height: '36px',
          }}
        >
          {/* 項目名 */}
          <Box
            sx={{
              flex: 4,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {!disabled ? (
              <IconButton
                sx={{
                  ...styles.iconButton,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  padding: '2px',
                }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon sx={styles.dragButton} />
              </IconButton>
            ) : (
              <Box sx={{ width: '26px' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0, opacity: isActive ? 1 : 0.5 }}>
              <Controller
                render={({ field }): JSX.Element => (
                  <CustomTextfield
                    {...field}
                    error={hasError}
                    formMb="0px"
                    labelText=""
                    inputWidth="100%"
                    height="36px"
                    inputPaddingIs10px8px
                    disabled={disabled || !isActive}
                    borderColor="#CAD5E2"
                  />
                )}
                control={control}
                name={`${fieldName}.${parentIndex}.children.${childIndex}.childValue`}
              />
            </Box>
          </Box>
          {/* データの種類 */}
          <Box sx={{ flex: 3, minWidth: 0, opacity: isActive ? 1 : 0.5 }}>
            <Controller
              render={({ field, fieldState }): JSX.Element => (
                <CustomSelectField
                  {...field}
                  error={validated && Boolean(fieldState.error)}
                  formMb="0px"
                  labelText=""
                  height="36px"
                  placeholder="未選択"
                  // 明細の列では選択肢型を選べない（#3075）
                  list={childDataTypeOptions}
                  width="100%"
                  disabled={disabled || !isActive}
                  borderColor="#CAD5E2"
                />
              )}
              control={control}
              name={`${fieldName}.${parentIndex}.children.${childIndex}.type`}
            />
          </Box>
          {/* 列幅 */}
          <Box sx={{ flex: 2, minWidth: 0, opacity: isActive ? 1 : 0.5 }}>
            <Controller
              render={({ field, fieldState }): JSX.Element => (
                <CustomSelectField
                  {...field}
                  error={validated && Boolean(fieldState.error)}
                  formMb="0px"
                  labelText=""
                  height="36px"
                  placeholder="未選択"
                  list={displayWidthOptions}
                  width="100%"
                  disabled={disabled || !isActive}
                  borderColor="#CAD5E2"
                />
              )}
              control={control}
              name={`${fieldName}.${parentIndex}.children.${childIndex}.displayWidth`}
              defaultValue={displayWidth.medium}
            />
          </Box>
          {/* アイコングループ */}
          <Box
            sx={{
              width: '104px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {!disabled && (
              <Box sx={styles.buttonGroup}>
                {/* 詳細設定ボタン。
                    選択肢型（#3075）なのに選択肢が未設定なら、モーダルを開いて設定するよう促す */}
                <Tooltip title={needsChoiceSetup ? '選択肢を設定してください' : '詳細設定'}>
                  <IconButton
                    sx={{
                      ...styles.iconButton,
                      ...styles.detailButton,
                      ...(needsChoiceSetup ? { color: orange50 } : {}),
                    }}
                    onClick={(): void => {
                      onEditItem?.({
                        isChild: true,
                        parentIndex,
                        childIndex,
                      });
                    }}
                  >
                    {needsChoiceSetup ? (
                      <ErrorOutlineIcon sx={{ fontSize: '18px' }} />
                    ) : (
                      <TuneIcon sx={{ fontSize: '18px' }} />
                    )}
                  </IconButton>
                </Tooltip>
                {/* マイナスボタン：既存項目の場合はモーダル表示 */}
                <Tooltip title="項目を削除する">
                  <span>
                    <IconButton
                      sx={{
                        ...styles.iconButton,
                        ...styles.removeButton,
                      }}
                      onClick={(): void => {
                        const isExisting = existingItemIds.includes(currentChild?.id);
                        if (isExisting && onDeleteRequest) {
                          onDeleteRequest({
                            id: currentChild?.id,
                            itemName: currentChild?.childValue || '',
                            isChild: true,
                            parentIndex,
                            childIndex,
                          });
                        } else {
                          onRemoveChild(parentIndex, childIndex);
                        }
                      }}
                    >
                      <MinusIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="下に項目を追加する">
                  <IconButton
                    sx={{ ...styles.iconButton, ...styles.addButton }}
                    onClick={(): void => onInsertChild(parentIndex, childIndex)}
                  >
                    <PlusIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        {hasAnyError && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', marginTop: '4px' }}>
            <Box sx={{ flex: 4, minWidth: 0, marginLeft: '28px' }}>
              <Typography
                sx={{
                  color: '#BD0017',
                  fontSize: '12px',
                  textAlign: 'left',
                  visibility: hasError ? 'visible' : 'hidden',
                }}
              >
                {errorMessage || '\u00A0'}
              </Typography>
            </Box>
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#BD0017',
                  fontSize: '12px',
                  textAlign: 'left',
                  visibility: hasTypeError ? 'visible' : 'hidden',
                }}
              >
                {typeErrorMessage || '\u00A0'}
              </Typography>
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#BD0017',
                  fontSize: '12px',
                  textAlign: 'left',
                  visibility: hasDisplayWidthError ? 'visible' : 'hidden',
                }}
              >
                {displayWidthErrorMessage || '\u00A0'}
              </Typography>
            </Box>
            <Box sx={{ width: '104px', flexShrink: 0 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// 子要素の並び替え用の独立したDndContext
interface ChildDndContextProps {
  index: number;
  fieldName: string;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  trigger: UseFormTrigger<FormData>;
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  disabled: boolean;
  validated: boolean;
  onRemoveChild: (parentIndex: number, childIndex: number) => void;
  onInsertChild: (parentIndex: number, childIndex: number) => void;
  existingItemIds?: string[];
  onDeleteRequest?: (data: DeleteItemData) => void;
  onEditItem?: (data: EditItemRequest) => void;
}

const ChildDndContext: React.FC<ChildDndContextProps> = ({
  index,
  fieldName,
  watch,
  setValue,
  trigger,
  control,
  errors,
  disabled,
  validated,
  onRemoveChild,
  onInsertChild,
  existingItemIds = [],
  onDeleteRequest,
  onEditItem,
}) => {
  const childSensors = useSensors(useSensor(PointerSensor));

  const handleChildDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const children = watch(fieldName)?.[index]?.children;
    if (!children) return;

    // idは追加直後・初回登録時に全て '0' のため、配列位置（childIndex）でキーを構成する
    const activeChildIndex = children.findIndex(
      (_child: ItemListChildType, childIndex: number) =>
        `child-${index}-${childIndex}` === active.id,
    );
    const overChildIndex = children.findIndex(
      (_child: ItemListChildType, childIndex: number) => `child-${index}-${childIndex}` === over.id,
    );

    if (activeChildIndex !== -1 && overChildIndex !== -1) {
      const newChildren = arrayMove(children, activeChildIndex, overChildIndex);
      const current = watch(fieldName) as ItemListType[];
      const newList = current.map((item: ItemListType, idx: number) =>
        idx === index ? { ...item, children: newChildren } : item,
      );
      setValue(fieldName, newList);
      trigger(fieldName);
    }
  };

  return (
    <Box sx={{ marginTop: '10px' }}>
      <DndContext
        sensors={childSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleChildDragEnd}
      >
        <SortableContext
          items={watch(fieldName)?.[index]?.children.map(
            (_child: ItemListChildType, childIndex: number) => `child-${index}-${childIndex}`,
          )}
          strategy={verticalListSortingStrategy}
        >
          {watch(fieldName)?.[index]?.children?.map(
            (_childItem: ItemListChildType, childIndex: number) => {
              const childKey = `child-${index}-${childIndex}`;
              return (
                <SortableChildItem
                  key={childKey}
                  id={childKey}
                  parentIndex={index}
                  childIndex={childIndex}
                  control={control}
                  errors={errors}
                  fieldName={fieldName}
                  disabled={disabled}
                  validated={validated}
                  watch={watch}
                  onRemoveChild={onRemoveChild}
                  onInsertChild={onInsertChild}
                  existingItemIds={existingItemIds}
                  onDeleteRequest={onDeleteRequest}
                  onEditItem={onEditItem}
                />
              );
            },
          )}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

// ソート可能なアイテムコンポーネント
interface SortableItemProps {
  id: string;
  index: number;
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  fieldName: string;
  disabled: boolean;
  validated: boolean;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  trigger: UseFormTrigger<FormData>;
  onRemove: (index: number) => void;
  onInsert: (index: number) => void;
  onAddChild: (index: number) => void;
  onRemoveChild: (parentIndex: number, childIndex: number) => void;
  onInsertChild: (parentIndex: number, childIndex: number) => void;
  existingItemIds?: string[];
  onDeleteRequest?: (data: DeleteItemData) => void;
  onEditItem?: (data: EditItemRequest) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  index,
  control,
  errors,
  fieldName,
  disabled,
  validated,
  watch,
  setValue,
  trigger,
  onRemove,
  onInsert,
  onAddChild,
  onRemoveChild,
  onInsertChild,
  existingItemIds = [],
  onDeleteRequest,
  onEditItem,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  // 現在の項目の状態を取得
  const currentItem = watch(fieldName)?.[index];
  const isActive = currentItem?.is_active ?? true; // デフォルトはtrue

  // 選択肢型なのに選択肢が未設定か（#3075）。
  // 行のセレクトから型を変えただけでは選択肢が空のままになるため、詳細設定を促す
  const needsChoiceSetup =
    isChoiceDataType(currentItem?.type) && (currentItem?.choices?.length ?? 0) === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemErrors = (errors[fieldName] as any)?.[index];
  const hasError = validated && Boolean(itemErrors?.parentValue);
  const errorMessage = itemErrors?.parentValue?.message;
  // 型フィールドのエラーをuseControllerで取得（errors.typeとFieldError.typeの名前衝突回避）
  const typeControllerName = `${fieldName}.${index}.type`;
  const { fieldState: typeFieldState } = useController({
    control,
    name: typeControllerName,
  });
  const hasTypeError = validated && Boolean(typeFieldState.error);
  const typeErrorMessage = typeFieldState.error?.message;
  const hasAnyError = hasError || hasTypeError;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1, // ドラッグ中のみ半透明（OFFでも行全体は薄くしない）
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ marginBottom: '10px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #CAD5E2',
          borderRadius: '8px',
          padding: '12px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            height: '36px',
          }}
        >
          {/* 項目名 */}
          <Box
            sx={{
              flex: 4,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {!disabled ? (
              <IconButton
                sx={{
                  ...styles.iconButton,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  padding: '2px',
                }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon sx={styles.dragButton} />
              </IconButton>
            ) : (
              <Box sx={{ width: '26px' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0, opacity: isActive ? 1 : 0.5 }}>
              <Controller
                render={({ field }): JSX.Element => (
                  <CustomTextfield
                    {...field}
                    error={hasError}
                    formMb="0px"
                    labelText=""
                    height="36px"
                    inputWidth="100%"
                    inputPaddingIs10px8px
                    disabled={disabled || !isActive}
                    borderColor="#CAD5E2"
                  />
                )}
                control={control}
                name={`${fieldName}.${index}.parentValue`}
              />
            </Box>
          </Box>
          {/* データの種類 */}
          <Box sx={{ flex: 3, minWidth: 0, opacity: isActive ? 1 : 0.5 }}>
            <Controller
              render={({ field, fieldState }): JSX.Element => (
                <CustomSelectField
                  {...field}
                  error={validated && Boolean(fieldState.error)}
                  formMb="0px"
                  labelText=""
                  height="36px"
                  placeholder="未選択"
                  list={dataTypeOptions}
                  width="100%"
                  disabled={
                    disabled ||
                    !isActive ||
                    (watch(fieldName)?.[index]?.children &&
                      watch(fieldName)?.[index]?.children.length > 0)
                  }
                  borderColor="#CAD5E2"
                />
              )}
              control={control}
              name={`${fieldName}.${index}.type`}
            />
          </Box>
          {/* アイコングループ */}
          <Box sx={{ width: '140px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {!disabled && (
              <Box sx={styles.buttonGroup}>
                {/* 詳細設定ボタン。
                    選択肢型（#3075）なのに選択肢が未設定なら、モーダルを開いて設定するよう促す。
                    行のセレクトから型を変えただけだとこの状態になるため、気づけるようにしている */}
                <Tooltip title={needsChoiceSetup ? '選択肢を設定してください' : '詳細設定'}>
                  <IconButton
                    sx={{
                      ...styles.iconButton,
                      ...styles.detailButton,
                      ...(needsChoiceSetup ? { color: orange50 } : {}),
                    }}
                    onClick={(): void => {
                      onEditItem?.({
                        isChild: false,
                        parentIndex: index,
                      });
                    }}
                  >
                    {needsChoiceSetup ? (
                      <ErrorOutlineIcon sx={{ fontSize: '18px' }} />
                    ) : (
                      <TuneIcon sx={{ fontSize: '18px' }} />
                    )}
                  </IconButton>
                </Tooltip>
                {/* マイナスボタン：既存項目の場合はモーダル表示 */}
                <Tooltip title="項目を削除する">
                  <span>
                    <IconButton
                      sx={{
                        ...styles.iconButton,
                        ...styles.removeButton,
                      }}
                      onClick={(): void => {
                        const isExisting = existingItemIds.includes(currentItem?.id);
                        if (isExisting && onDeleteRequest) {
                          onDeleteRequest({
                            id: currentItem?.id,
                            itemName: currentItem?.parentValue || '',
                            isChild: false,
                            parentIndex: index,
                          });
                        } else {
                          onRemove(index);
                        }
                      }}
                    >
                      <MinusIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="下に項目を追加する">
                  <IconButton
                    sx={{ ...styles.iconButton, ...styles.addButton }}
                    onClick={(): void => onInsert(index + 1)}
                  >
                    <PlusIcon />
                  </IconButton>
                </Tooltip>
                {/* 子項目追加ボタン */}
                <Tooltip title="子項目を追加する">
                  <IconButton
                    sx={{ ...styles.iconButton, ...styles.addArrowIcon }}
                    onClick={(): void => onAddChild(index)}
                  >
                    <ArrowIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        {hasAnyError && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', marginTop: '4px' }}>
            <Box sx={{ flex: 4, minWidth: 0, marginLeft: '28px' }}>
              <Typography
                sx={{
                  color: '#BD0017',
                  fontSize: '12px',
                  textAlign: 'left',
                  visibility: hasError ? 'visible' : 'hidden',
                }}
              >
                {errorMessage || '\u00A0'}
              </Typography>
            </Box>
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#BD0017',
                  fontSize: '12px',
                  textAlign: 'left',
                  visibility: hasTypeError ? 'visible' : 'hidden',
                }}
              >
                {typeErrorMessage || '\u00A0'}
              </Typography>
            </Box>
            <Box sx={{ width: '140px', flexShrink: 0 }} />
          </Box>
        )}
      </Box>
      {/* 子入力欄 - 独立したDndContextで子要素の並び替えを管理 */}
      {watch(fieldName)?.[index]?.children && watch(fieldName)?.[index]?.children.length > 0 && (
        <ChildDndContext
          index={index}
          fieldName={fieldName}
          watch={watch}
          setValue={setValue}
          trigger={trigger}
          control={control}
          errors={errors}
          disabled={disabled}
          validated={validated}
          onRemoveChild={onRemoveChild}
          onInsertChild={onInsertChild}
          existingItemIds={existingItemIds}
          onDeleteRequest={onDeleteRequest}
          onEditItem={onEditItem}
        />
      )}
    </Box>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const ItemListManager: React.FC<ItemListManagerProps> = ({
  control,
  errors,
  setValue,
  trigger,
  watch,
  disabled = false,
  titleShow = true,
  fieldName = 'itemList',
  validated = false,
  existingItemIds = [],
  onReRead,
  onResetRequest,
  onDeleteRequest,
  onItemLimitExceeded,
  onItemInserted,
  canImportJson = false,
  canCopyJson = false,
  isEditMode = false,
  onShowMessage,
  patternMasters = [],
  patternDictionaries = [],
}) => {
  // items 配列を管理するための useFieldArray フック
  const { fields } = useFieldArray({
    control,
    name: fieldName,
  });

  // 詳細設定モーダルの状態管理
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [editModalData, setEditModalData] = React.useState<EditItemData | null>(null);
  // 計算フィールド設定用のコンテキスト（#3145）。モーダルを開くたびに対象項目に合わせて作り直す
  const [editModalCalcContext, setEditModalCalcContext] =
    React.useState<CalculationEditContext | null>(null);

  // JSON取り込みモーダルの状態管理
  const [jsonImportOpen, setJsonImportOpen] = React.useState(false);
  const [jsonInputText, setJsonInputText] = React.useState('');
  const [jsonImportError, setJsonImportError] = React.useState('');
  // #3039: 差分プレビューの状態。jsonPendingList が非nullの間はプレビュー画面を表示する
  const [jsonPendingList, setJsonPendingList] = React.useState<ItemListType[] | null>(null);
  const [jsonDiffEntries, setJsonDiffEntries] = React.useState<ItemDiffEntry[]>([]);
  // #3344: 取り込みで外れるマスタ照合・辞書変換の設定。差分プレビューで注意表示に使う
  const [jsonMappingNotices, setJsonMappingNotices] = React.useState<string[]>([]);
  // 行差分の表示に使う変更前後のJSON文字列
  const [jsonBeforeText, setJsonBeforeText] = React.useState('');
  const [jsonAfterText, setJsonAfterText] = React.useState('');

  // 項目リストを空1件にリセットして手動入力できる状態にする（#3147）
  // 呼び出し元が確認モーダルを挟む場合は onResetRequest 経由でこの処理を実行する
  const resetItemListToManualInput = (): void => {
    const emptyItem = {
      id: '0',
      parentValue: '',
      type: 'string',
      children: [],
      is_active: true,
      is_ocr_target: true, // デフォルトでOCR対象（#2968）
    };
    setValue(fieldName, [emptyItem]);
  };

  // 内部値（string, number等）→ 表示名（テキスト、数字等）の変換
  const typeValueToLabel = (value: string): string => {
    const found = dataTypeOptions.find(opt => opt.value === value);
    return found?.option || 'テキスト';
  };
  // 表示名 → 内部値の変換
  const typeLabelToValue = (label: string): string => {
    const found = dataTypeOptions.find(opt => opt.option === label);
    return found?.value || 'string';
  };

  // 詳細設定ボタンのハンドラー
  const handleEditItem = (request: EditItemRequest): void => {
    // フィールドコード未設定の項目（既存パターン含む）にここで自動採番する（#3145）。
    // 計算式の参照先コードをモーダルを開いた時点で確定させるため
    // （採番を保存時まで遅らせると、その間の項目名変更でコードが変わり式が壊れる）。
    // 採番結果は保存時の fillMissingFieldCodes と同じ値で、Firestore へはパターン保存まで書かれない
    const current = fillMissingFieldCodes(watch(fieldName) as ItemListType[]);
    setValue(fieldName, current);
    setEditModalCalcContext(
      buildCalculationEditContext(current, {
        isChild: request.isChild,
        parentIndex: request.parentIndex,
        childIndex: request.childIndex,
      }),
    );
    // 編集対象「以外」が使っているフィールドコードを集める（重複チェック用・#3175）
    const collectUsedFieldCodes = (excludeId: string): string[] => {
      const codes: string[] = [];
      current.forEach((item: ItemListType) => {
        if (item.id !== excludeId && item.field_code) codes.push(item.field_code);
        item.children?.forEach((child: ItemListChildType) => {
          if (child.id !== excludeId && child.field_code) codes.push(child.field_code);
        });
      });
      return codes;
    };

    if (request.isChild && request.childIndex !== undefined) {
      const parent = current[request.parentIndex];
      const child = parent?.children?.[request.childIndex];
      if (!child) return;
      setEditModalData({
        id: child.id,
        itemName: child.childValue,
        fieldCode: child.field_code || '',
        // 未設定時の初期値を `親のコード.子の名前` にするため（#3175）。
        // 親のコードも未設定なら項目名から作り、保存時の採番と同じ値にする
        parentFieldCode: parent.field_code || sanitizeFieldCode(parent.parentValue),
        usedFieldCodes: collectUsedFieldCodes(child.id),
        dataType: typeValueToLabel(child.type || 'string'),
        displayWidth: child.displayWidth || displayWidth.medium,
        isChild: true,
        parentId: parent.id,
        listIndex: request.parentIndex,
        childIndex: request.childIndex,
        isOcrTarget: child.is_ocr_target ?? true,
        extractionRule: child.extractionRule || '',
        outputFormat: child.outputFormat,
        fixedValue: child.fixedValue || '',
        choices: child.choices ?? [], // 選択肢（#3075）。明細の列はプルダウンのみ
        required: child.required,
        minLength: child.minLength,
        maxLength: child.maxLength,
        minValue: child.minValue,
        maxValue: child.maxValue,
        treatZeroAsEmpty: child.treatZeroAsEmpty, // 0を空欄扱い（#3262）
        // 文字種・入力パターン・日付範囲（#3357）
        charType: child.charType,
        reviewChoiceValues: child.reviewChoiceValues, // 要注意にする選択肢（#3357）
        reviewLowConfidence: child.reviewLowConfidence, // 読み取り精度の要注意（#3384）
        pattern: child.pattern,
        patternMessage: child.patternMessage,
        dateNoPast: child.dateNoPast,
        dateNoFuture: child.dateNoFuture,
        datePastLimit: child.datePastLimit,
        datePastUnit: child.datePastUnit,
        dateFutureLimit: child.dateFutureLimit,
        dateFutureUnit: child.dateFutureUnit,
        isCalculated: child.is_calculated,
        formula: child.formula,
      });
    } else {
      const item = current[request.parentIndex];
      if (!item) return;
      setEditModalData({
        id: item.id,
        itemName: item.parentValue,
        fieldCode: item.field_code || '',
        usedFieldCodes: collectUsedFieldCodes(item.id),
        dataType: typeValueToLabel(item.type || 'string'),
        displayWidth: displayWidth.medium,
        isChild: false,
        listIndex: request.parentIndex,
        isOcrTarget: item.is_ocr_target ?? true,
        extractionRule: item.extractionRule || '',
        outputFormat: item.outputFormat,
        fixedValue: item.fixedValue || '',
        choices: item.choices ?? [], // 選択肢（#3075）
        required: item.required,
        minLength: item.minLength,
        maxLength: item.maxLength,
        minValue: item.minValue,
        maxValue: item.maxValue,
        treatZeroAsEmpty: item.treatZeroAsEmpty, // 0を空欄扱い（#3262）
        // 文字種・入力パターン・日付範囲（#3357）
        charType: item.charType,
        reviewChoiceValues: item.reviewChoiceValues, // 要注意にする選択肢（#3357）
        reviewLowConfidence: item.reviewLowConfidence, // 読み取り精度の要注意（#3384）
        pattern: item.pattern,
        patternMessage: item.patternMessage,
        dateNoPast: item.dateNoPast,
        dateNoFuture: item.dateNoFuture,
        datePastLimit: item.datePastLimit,
        datePastUnit: item.datePastUnit,
        dateFutureLimit: item.dateFutureLimit,
        dateFutureUnit: item.dateFutureUnit,
        isCalculated: item.is_calculated,
        formula: item.formula,
      });
    }
    setEditModalOpen(true);
  };

  // 詳細設定モーダルの保存ハンドラー
  const handleEditModalSave = (data: EditItemData): void => {
    const current = watch(fieldName) as ItemListType[];
    // フィールドコードが変更されたら、そのコードを参照している計算式を自動で書き換える（#3145）
    const applyFieldCodeRename = (
      list: ItemListType[],
      previousCode: string | undefined,
    ): ItemListType[] => {
      if (!previousCode || !data.fieldCode || previousCode === data.fieldCode) return list;
      return renameFieldCodeReferences(list, previousCode, data.fieldCode);
    };
    if (data.isChild && data.listIndex !== undefined && data.childIndex !== undefined) {
      // 子項目の更新（idは初回登録時に全て '0' のため、childIndexで特定する）
      const parentIndex = data.listIndex;
      const childIndex = data.childIndex;
      if (childIndex >= 0 && childIndex < (current[parentIndex]?.children?.length ?? 0)) {
        const previousCode = current[parentIndex]?.children?.[childIndex]?.field_code;
        const newList = current.map((item: ItemListType, idx: number) =>
          idx === parentIndex
            ? {
                ...item,
                children: item.children.map((child: ItemListChildType, ci: number) => {
                  if (ci !== childIndex) return child;
                  const nextChildType = typeLabelToValue(data.dataType);
                  return {
                    ...child,
                    childValue: data.itemName,
                    field_code: data.fieldCode, // #3175
                    type: nextChildType,
                    displayWidth: data.displayWidth,
                    is_ocr_target: data.isOcrTarget ?? true,
                    extractionRule: data.extractionRule || '',
                    outputFormat: data.outputFormat,
                    fixedValue: data.fixedValue || '',
                    // 選択肢（#3075）。選択肢型以外に変更した場合は空配列でクリアする
                    choices: isChoiceDataType(nextChildType)
                      ? sanitizeChoices(data.choices ?? [])
                      : [],
                    required: data.required,
                    minLength: data.minLength,
                    maxLength: data.maxLength,
                    minValue: data.minValue,
                    maxValue: data.maxValue,
                    treatZeroAsEmpty: data.treatZeroAsEmpty, // 0を空欄扱い（#3262）
                    // 文字種・入力パターン・日付範囲（#3357）
                    charType: data.charType,
                    reviewChoiceValues: data.reviewChoiceValues, // 要注意にする選択肢（#3357）
                    reviewLowConfidence: data.reviewLowConfidence, // 読み取り精度の要注意（#3384）
                    pattern: data.pattern,
                    patternMessage: data.patternMessage,
                    dateNoPast: data.dateNoPast,
                    dateNoFuture: data.dateNoFuture,
                    datePastLimit: data.datePastLimit,
                    datePastUnit: data.datePastUnit,
                    dateFutureLimit: data.dateFutureLimit,
                    dateFutureUnit: data.dateFutureUnit,
                    // 計算フィールド設定（#3145）
                    is_calculated: data.isCalculated ?? false,
                    formula: data.formula ?? '',
                  };
                }),
              }
            : item,
        );
        setValue(fieldName, applyFieldCodeRename(newList, previousCode));
      }
    } else if (data.listIndex !== undefined) {
      // 親項目の更新
      const previousCode = current[data.listIndex]?.field_code;
      const newList = current.map((item: ItemListType, idx: number) => {
        if (idx !== data.listIndex) return item;
        // 子項目を持つ親は複数行データ（array）に強制されるため、選択肢は保持しない（#3075）
        const nextType = item.children?.length > 0 ? 'array' : typeLabelToValue(data.dataType);
        return {
          ...item,
          parentValue: data.itemName,
          field_code: data.fieldCode, // #3175
          type: nextType,
          is_ocr_target: data.isOcrTarget ?? true,
          extractionRule: data.extractionRule || '',
          outputFormat: data.outputFormat,
          fixedValue: data.fixedValue || '',
          choices: isChoiceDataType(nextType) ? sanitizeChoices(data.choices ?? []) : [],
          required: data.required,
          minLength: data.minLength,
          maxLength: data.maxLength,
          minValue: data.minValue,
          maxValue: data.maxValue,
          treatZeroAsEmpty: data.treatZeroAsEmpty, // 0を空欄扱い（#3262）
          // 文字種・入力パターン・日付範囲（#3357）
          charType: data.charType,
          reviewChoiceValues: data.reviewChoiceValues, // 要注意にする選択肢（#3357）
          reviewLowConfidence: data.reviewLowConfidence, // 読み取り精度の要注意（#3384）
          pattern: data.pattern,
          patternMessage: data.patternMessage,
          dateNoPast: data.dateNoPast,
          dateNoFuture: data.dateNoFuture,
          datePastLimit: data.datePastLimit,
          datePastUnit: data.datePastUnit,
          dateFutureLimit: data.dateFutureLimit,
          dateFutureUnit: data.dateFutureUnit,
          // 計算フィールド設定（#3145）。テーブル親はモーダルに計算UIを出さないため既存値を保つ
          is_calculated:
            item.children?.length > 0 ? item.is_calculated : (data.isCalculated ?? false),
          formula: item.children?.length > 0 ? item.formula : (data.formula ?? ''),
        };
      });
      setValue(fieldName, applyFieldCodeRename(newList, previousCode));
    }
    if (validated) {
      trigger(fieldName);
    }
    setEditModalOpen(false);
    setEditModalData(null);
  };

  // ドラッグ&ドロップのセンサー設定（FileExportDialogと同様のシンプルな設定）
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // itemListのid・children.idをindex順に振り直すユーティリティ
  // 親: "1", "2", ... / 子: "2-1", "2-2", ... の形式

  // アイテム削除
  const handleRemoveItem = (index: number): void => {
    const current = watch(fieldName);
    const newList = current.filter((_: ItemListType, i: number) => i !== index);
    // UUID方式: 削除時はIDを変更しない
    setValue(fieldName, newList);
    if (validated) {
      trigger(fieldName);
    }
  };

  // 新規アイテム挿入
  const handleInsertItem = (insertIndex?: number): void => {
    const current = watch(fieldName) || [];
    // 項目数上限チェック（OCR対象項目数で判定。追加項目はデフォルトOCR対象。#3045）
    const ocrTargetCount = getOcrTargetItemCount(current);
    if (ocrTargetCount >= getMaxItemCount()) {
      onItemLimitExceeded?.();
      return;
    }
    const index = insertIndex !== undefined ? insertIndex : current.length;
    const newItem = {
      id: '0', // 保存時にnormalizeItemListIdsで適切なIDに再設定される
      parentValue: '',
      type: 'string',
      children: [],
      is_active: true, // デフォルトで有効
      is_ocr_target: true, // デフォルトでOCR対象（#2968）
    };
    const newList = [...current.slice(0, index), newItem, ...current.slice(index)];
    // UUID方式: 追加時はIDを変更しない（保存時に採番）
    setValue(fieldName, newList);
    trigger(fieldName);
    onItemInserted?.();
  };

  // 子アイテム追加
  const handleAddChild = (parentIndex: number): void => {
    const current = watch(fieldName);
    // 項目数上限チェック（OCR対象項目数で判定。追加項目はデフォルトOCR対象。#3045）
    const ocrTargetCount = getOcrTargetItemCount(current);
    if (ocrTargetCount >= getMaxItemCount()) {
      onItemLimitExceeded?.();
      return;
    }
    const newChild = {
      id: '0', // 保存時にnormalizeItemListIdsで適切なIDに再設定される
      childValue: '',
      type: 'string',
      displayWidth: displayWidth.medium,
      is_active: true, // デフォルトで有効
      is_ocr_target: true, // デフォルトでOCR対象（#2968）
    };
    const newList = current.map((item: ItemListType, idx: number) =>
      idx === parentIndex
        ? {
            ...item,
            // 子要素を持つ親項目は必ず配列型にする
            type: 'array',
            // 選択肢型から配列型に変わるため、古い選択肢は残さない（#3075）
            choices: [],
            children: [...(item.children || []), newChild],
          }
        : item,
    );
    // UUID方式: 追加時はIDを変更しない（保存時に採番）
    setValue(fieldName, newList);
    trigger(fieldName);
    onItemInserted?.();
  };

  // データ型の表記ゆれを内部値へ正規化（JSON取り込み用）
  const jsonTypeMap: Record<string, string> = {
    string: 'string',
    text: 'string',
    テキスト: 'string',
    // #3075: textarea が漏れており、JSON往復でテキストエリア型がテキストに落ちていたため追加
    textarea: 'textarea',
    テキストエリア: 'textarea',
    number: 'number',
    数字: 'number',
    数値: 'number',
    date: 'date',
    日付: 'date',
    array: 'array',
    配列: 'array',
    // 選択肢型（#3075）
    radio: 'radio',
    ラジオボタン: 'radio',
    select: 'select',
    プルダウン: 'select',
  };
  const resolveJsonType = (t: unknown): string =>
    typeof t === 'string' && jsonTypeMap[t] ? jsonTypeMap[t] : 'string';

  // 子項目（明細の列）のデータ型。ラジオボタンは幅を取り明細で扱えないためテキストへ倒す（#3075）
  const resolveJsonChildType = (t: unknown): string => {
    const resolved = resolveJsonType(t);
    return parentOnlyDataTypes.includes(resolved) ? 'string' : resolved;
  };

  // 列幅をJSONから正規化（1:大, 2:中, 3:小 以外は中にフォールバック）
  const resolveJsonDisplayWidth = (w: unknown): number =>
    w === displayWidth.large || w === displayWidth.medium || w === displayWidth.small
      ? w
      : displayWidth.medium;

  // フィールドコードをJSONから正規化（#3175）。
  // パターンの複製時に参照を保つため引き継ぐが、外部から編集されたJSONも取り込めるため
  // 形式が不正なものは捨てて、保存時の自動採番に任せる。
  // 取り込み後に他項目と重複した場合も、保存時のバリデーションで検出される。
  const resolveJsonFieldCode = (code: unknown): string | undefined => {
    if (typeof code !== 'string') return undefined;
    const trimmed = code.trim();
    if (trimmed === '') return undefined;
    return validateFieldCodeFormat(trimmed) === null ? trimmed : undefined;
  };

  // 固定値をJSONから正規化（#3073）。数値で書かれていても文字列として扱う（例: "fixedValue": 0）。
  // テーブル型（array）は固定値の対象外のため空にする（項目編集モーダルの扱いと揃える）
  const resolveJsonFixedValue = (v: unknown, isArrayType: boolean): string => {
    if (isArrayType) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return String(v);
    return '';
  };

  // 選択肢をJSONから正規化（#3075）。
  // 選択肢型（radio / select）以外の項目には持たせない（項目編集モーダルの扱いと揃える）。
  // 文字列配列の簡易記法（["有", "無"]）や数値の value も normalizeChoiceList が吸収する
  const resolveJsonChoices = (v: unknown, type: string): ItemChoiceType[] =>
    isChoiceDataType(type) ? normalizeChoiceList(v) : [];

  // 計算フィールドをJSONから正規化（#3145 / #3195）。
  // 計算できない型（日付・選択肢型・複数行データ）には持たせない。
  // 式が空文字なら計算OFF扱いにする（保存時のバリデーションで弾かれる状態を作らない）。
  // 不正な式はここでは判定せず、保存時の getCalculationSettingsError に委ねる
  const resolveJsonCalculation = (
    src: Record<string, unknown>,
    type: string,
  ): { is_calculated: boolean; formula: string } => {
    if (!isCalculatableDataType(type) || pickRaw(src, 'is_calculated', 'isCalculated') !== true) {
      return { is_calculated: false, formula: '' };
    }
    const formula = pickString(src, 'formula', 'formula').trim();
    if (formula === '') return { is_calculated: false, formula: '' };
    return { is_calculated: true, formula };
  };

  // 項目IDをJSONから正規化（#3039）。
  // 環境間で設定をコピーする際にIDを維持するため、JSONに書かれたIDをそのまま採用する。
  // 未指定・空文字のときは新規項目マーカー '0' を返し、保存時の normalizeItemListIds に採番を委ねる。
  // 数値で書かれていても受け付ける（例: "id": 2）
  const resolveJsonItemId = (v: unknown): string => {
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number') return String(v);
    return '0';
  };

  // バリデーション制約をJSONから抽出（#2815対応）。
  // キーは snake_case を正とし、旧 camelCase 表記も読めるようにする（#3195）
  const extractValidation = (
    src: Record<string, unknown>,
  ): {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    treatZeroAsEmpty?: boolean;
    charType?: ItemCharType;
    pattern?: string;
    patternMessage?: string;
    dateNoPast?: boolean;
    dateNoFuture?: boolean;
    datePastLimit?: number;
    datePastUnit?: DateRangeUnit;
    dateFutureLimit?: number;
    dateFutureUnit?: DateRangeUnit;
    reviewChoiceValues?: string[];
    reviewLowConfidence?: boolean;
  } => ({
    required: typeof src.required === 'boolean' ? src.required : undefined,
    minLength: pickNumber(src, 'min_length', 'minLength'),
    maxLength: pickNumber(src, 'max_length', 'maxLength'),
    minValue: pickNumber(src, 'min_value', 'minValue'),
    maxValue: pickNumber(src, 'max_value', 'maxValue'),
    // 0を空欄扱い（#3262）
    treatZeroAsEmpty: pickBoolean(src, 'treat_zero_as_empty', 'treatZeroAsEmpty'),
    // 文字種・入力パターン・日付範囲・要注意選択肢（#3357 / #3372）。
    // これらを読まないと、JSONを開いてそのまま反映するだけで設定が消える。
    // 不正な列挙値・型違いは undefined へ倒し、保存時バリデーションに委ねる（既存方針）
    charType: pickEnum(src, 'char_type', 'charType', Object.values(itemCharType)),
    pattern: pickString(src, 'pattern', 'pattern') || undefined,
    patternMessage: pickString(src, 'pattern_message', 'patternMessage') || undefined,
    dateNoPast: pickBoolean(src, 'date_no_past', 'dateNoPast'),
    dateNoFuture: pickBoolean(src, 'date_no_future', 'dateNoFuture'),
    datePastLimit: pickNumber(src, 'date_past_limit', 'datePastLimit'),
    datePastUnit: pickEnum(src, 'date_past_unit', 'datePastUnit', Object.values(dateRangeUnit)),
    dateFutureLimit: pickNumber(src, 'date_future_limit', 'dateFutureLimit'),
    dateFutureUnit: pickEnum(
      src,
      'date_future_unit',
      'dateFutureUnit',
      Object.values(dateRangeUnit),
    ),
    reviewChoiceValues: pickStringArray(src, 'review_choice_values', 'reviewChoiceValues'),
    // 読み取り精度の要注意（#3384）
    reviewLowConfidence: pickBoolean(src, 'review_low_confidence', 'reviewLowConfidence'),
  });

  // JSON取り込み処理
  const handleJsonImport = (): void => {
    setJsonImportError('');
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInputText);
    } catch {
      setJsonImportError('JSONの形式が正しくありません。');
      return;
    }
    if (!Array.isArray(parsed)) {
      setJsonImportError('JSONは配列形式で入力してください。');
      return;
    }

    const newList: ItemListType[] = [];
    for (let idx = 0; idx < parsed.length; idx++) {
      const item = parsed[idx] as Record<string, unknown>;
      if (
        typeof item !== 'object' ||
        item === null ||
        typeof item.name !== 'string' ||
        !item.name.trim()
      ) {
        setJsonImportError('各項目に "name" (string) が必要です。');
        return;
      }
      // #3039: 配列インデックスからの強制採番をやめ、JSONに書かれたIDを尊重する。
      // 未指定なら '0'（新規項目）として保存時の normalizeItemListIds に採番を委ねる
      const parentId = resolveJsonItemId(item.id);

      const children: ItemListChildType[] = Array.isArray(item.children)
        ? item.children
            .filter(
              (c: unknown) =>
                typeof c === 'object' &&
                c !== null &&
                typeof (c as Record<string, unknown>).name === 'string' &&
                ((c as Record<string, unknown>).name as string).trim(),
            )
            .map((c: Record<string, unknown>) => {
              // #3075: 明細の列では選択肢型を扱えないためテキストへ倒す
              const childType = resolveJsonChildType(c.type);
              return {
                // #3039: 親項目と同じく、JSONに書かれたIDを尊重する
                id: resolveJsonItemId(c.id),
                childValue: (c.name as string).trim(),
                // #3175: フィールドコードを引き継ぐ。未指定・不正な値のときは undefined にし、
                // 保存時の自動採番（fillMissingFieldCodes）に任せる
                field_code: resolveJsonFieldCode(pickRaw(c, 'field_code', 'fieldCode')),
                type: childType,
                displayWidth: resolveJsonDisplayWidth(pickRaw(c, 'display_width', 'displayWidth')),
                is_active: pickRaw(c, 'is_active', 'isActive') !== false,
                is_ocr_target: pickRaw(c, 'is_ocr_target', 'isOcrTarget') !== false,
                extractionRule: pickString(c, 'extraction_rule', 'extractionRule'),
                // #3144: 選択肢外の出力形式はデフォルトへフォールバックさせる。
                // 日付型は未指定でもデフォルトを補い、表示・出力の形式を揃える。
                outputFormat: resolveDateOutputFormat(
                  pickRaw(c, 'output_format', 'outputFormat'),
                  childType === 'date',
                ),
                fixedValue: resolveJsonFixedValue(
                  pickRaw(c, 'fixed_value', 'fixedValue'),
                  childType === 'array',
                ),
                // 選択肢（#3075）。選択肢型以外は空配列になる
                choices: resolveJsonChoices(c.choices, childType),
                // 計算フィールド（#3145 / #3195）。計算できない型なら落とす
                ...resolveJsonCalculation(c, childType),
                ...extractValidation(c),
              };
            })
        : [];

      // 子項目がある場合は親自身が配列型になるため、日付型かどうかは解決後の型で判定する
      const parentType = children.length > 0 ? 'array' : resolveJsonType(item.type);

      newList.push({
        id: parentId,
        parentValue: item.name.trim(),
        // #3175: 上記の子項目と同じ
        field_code: resolveJsonFieldCode(pickRaw(item, 'field_code', 'fieldCode')),
        type: parentType,
        displayWidth: resolveJsonDisplayWidth(pickRaw(item, 'display_width', 'displayWidth')),
        children,
        is_active: pickRaw(item, 'is_active', 'isActive') !== false,
        is_ocr_target: pickRaw(item, 'is_ocr_target', 'isOcrTarget') !== false,
        extractionRule: pickString(item, 'extraction_rule', 'extractionRule'),
        // #3144: 選択肢外の出力形式はデフォルトへフォールバックさせる。
        // 日付型は未指定でもデフォルトを補い、表示・出力の形式を揃える。
        outputFormat: resolveDateOutputFormat(
          pickRaw(item, 'output_format', 'outputFormat'),
          parentType === 'date',
        ),
        fixedValue: resolveJsonFixedValue(
          pickRaw(item, 'fixed_value', 'fixedValue'),
          parentType === 'array',
        ),
        // 選択肢（#3075）。選択肢型以外は空配列になる
        choices: resolveJsonChoices(item.choices, parentType),
        // 計算フィールド（#3145 / #3195）。計算できない型なら落とす
        ...resolveJsonCalculation(item, parentType),
        ...extractValidation(item),
      });
    }

    if (newList.length === 0) {
      setJsonImportError('有効な項目が見つかりませんでした。');
      return;
    }

    // #3039: 項目IDの検証。不正なIDのまま反映すると既存データとの紐付けが壊れるため、
    // 差分プレビューへ進む前にここで弾く
    const currentList: ItemListType[] = watch(fieldName) || [];
    const idError = validateImportedItemIds(newList, currentList, isEditMode);
    if (idError) {
      setJsonImportError(idError);
      return;
    }

    // #3344: 照合キーに使われている項目が消される場合は差分プレビューへ進ませない。
    // 照合キーが欠けたマッピングは照合が成立しないため、先にマスタ側の設定を外してもらう。
    const searchKeyError = validateImportedSearchKeyItems(
      newList,
      currentList,
      patternMasters,
      patternDictionaries,
    );
    if (searchKeyError) {
      setJsonImportError(searchKeyError);
      return;
    }

    // #3039: 即座に反映せず、差分プレビューを表示して確認を求める。
    // 行差分は「現在の設定を出力したJSON」と「入力されたJSON」を比較する。
    // 入力テキストをそのまま使うと整形の違いが差分として出てしまうため、
    // 取り込み後のリストを同じ変換で文字列化して比較する
    setJsonPendingList(newList);
    setJsonDiffEntries(buildItemListDiff(currentList, newList));
    // #3344: 保存時に自動解除される参照を注意として示す（照合キーは上で弾いている）
    setJsonMappingNotices(
      collectImportMappingNotices(newList, currentList, patternMasters, patternDictionaries),
    );
    setJsonBeforeText(JSON.stringify(buildExportItems(), null, 2));
    setJsonAfterText(JSON.stringify(buildExportItemsFrom(newList), null, 2));
  };

  // #3039: 差分プレビューで確認後に項目リストへ反映する
  const handleJsonImportApply = (): void => {
    if (!jsonPendingList) return;
    setValue(fieldName, jsonPendingList);
    if (validated) {
      trigger(fieldName);
    }
    onItemInserted?.();
    closeJsonImportModal();
  };

  // 項目リストを出力用のプレーンオブジェクト配列へ変換する。
  // JSONコピー・編集モードの初期表示・行差分の生成（#3039）で共用する。
  // 同じ変換を通すことで、整形の違いが差分として現れないようにする
  // 出力用の変換は utils へ切り出した（#3372）。
  // 子パターンのJSON入力画面でも親パターンの設定JSONを参照用に出すため共用する
  const buildExportItemsFrom = (source: ItemListType[]): Record<string, unknown>[] =>
    buildItemListExport(source);

  // 現在フォームに入っている項目リストを出力用に変換する
  const buildExportItems = (): Record<string, unknown>[] =>
    buildExportItemsFrom(watch(fieldName) || []);

  // JSONコピー処理（現在の項目リストをJSON文字列にしてクリップボードへ）
  const handleJsonCopy = async (): Promise<void> => {
    const exportItems = buildExportItems();

    if (exportItems.length === 0) {
      onShowMessage?.('コピーできる項目がありません。');
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(exportItems, null, 2));
      onShowMessage?.('項目リストのJSONをコピーしました。');
    } catch {
      onShowMessage?.('クリップボードへのコピーに失敗しました。');
    }
  };

  // #3039: JSONモーダルを開く。編集モードでは現在の設定をJSONで初期表示し、
  // ユーザーがIDを保ったまま書き換えられるようにする
  const openJsonImportModal = (): void => {
    const exportItems = buildExportItems();
    setJsonInputText(
      isEditMode && exportItems.length > 0 ? JSON.stringify(exportItems, null, 2) : '',
    );
    setJsonImportError('');
    setJsonPendingList(null);
    setJsonDiffEntries([]);
    setJsonBeforeText('');
    setJsonAfterText('');
    setJsonImportOpen(true);
  };

  // #3039: JSONモーダルを閉じて状態をすべてクリアする
  const closeJsonImportModal = (): void => {
    setJsonImportOpen(false);
    setJsonInputText('');
    setJsonImportError('');
    setJsonPendingList(null);
    setJsonDiffEntries([]);
    setJsonMappingNotices([]);
    setJsonBeforeText('');
    setJsonAfterText('');
  };

  // ドラッグ&ドロップのハンドラー（FileExportDialogと同様のシンプルな並び替え）
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const current = watch(fieldName) as ItemListType[];
    const activeId = active.id as string;
    const overId = over.id as string;

    // 親要素のみ処理（子要素はChildDndContextが処理）
    const oldIndex = fields.findIndex(field => field.id.toString() === activeId);
    const newIndex = fields.findIndex(field => field.id.toString() === overId);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newList = arrayMove(current, oldIndex, newIndex);
      setValue(fieldName, newList);
      trigger(fieldName);
    }
  };

  return (
    <Box
      sx={{
        overflowX: 'auto',
        width: '100%',
        '@media screen and (max-width:960px)': {
          overflowX: 'scroll',
          minWidth: 0,
          '& > *': {
            minWidth: '500px',
          },
        },
      }}
    >
      {titleShow && (
        <Typography
          align="left"
          sx={{
            ...styles.formLabelWrapper,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          component="div"
        >
          <Typography color={gray80} sx={styles.label} component="span">
            取得する項目（50項目まで）
          </Typography>
        </Typography>
      )}

      {/* 項目設定のコツ・AIで再読取・リセットして手動入力ボタン */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px',
        }}
      >
        {/* 左側：項目設定のコツボタン */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
          }}
          onClick={(): void => {
            window.open(HELP_FAQ_ITEM_SETTING_TIPS, '_blank');
          }}
        >
          <QuestionIcon />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: orange50,
            }}
          >
            項目設定のコツ
          </Typography>
        </Box>

        {/* 右側：AIで再読取・リセットして手動入力（上段）／JSONコピー・取り込み（下段） */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '8px',
          }}
        >
          {/* 上段：AIで再読取・リセットして手動入力 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {onReRead && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={async (): Promise<void> => {
                    await onReRead();
                  }}
                >
                  <ReplayRoundedIcon sx={{ fontSize: '16px', color: orange50 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: orange50,
                    }}
                  >
                    AIで再読取
                  </Typography>
                </Box>
                {!disabled && (
                  <Box
                    sx={{
                      width: '1px',
                      height: '16px',
                      backgroundColor: '#CAD5E2',
                    }}
                  />
                )}
              </>
            )}
            {!disabled && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                }}
                onClick={(): void => {
                  // 編集時など、呼び出し元が確認を挟む場合はそちらに委譲する（#3147）
                  if (onResetRequest) {
                    onResetRequest();
                    return;
                  }
                  resetItemListToManualInput();
                }}
              >
                <ModeEditRoundedIcon sx={{ fontSize: '16px', color: orange50 }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: orange50,
                  }}
                >
                  リセットして手動入力
                </Typography>
              </Box>
            )}
          </Box>

          {/* 下段：JSONでコピー・JSONで取り込む（運営スタッフのみ） */}
          {(canCopyJson || (canImportJson && !disabled)) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {canCopyJson && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={async (): Promise<void> => {
                    await handleJsonCopy();
                  }}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: '16px', color: orange50 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: orange50,
                    }}
                  >
                    JSONでコピー
                  </Typography>
                </Box>
              )}
              {canCopyJson && canImportJson && !disabled && (
                <Box
                  sx={{
                    width: '1px',
                    height: '16px',
                    backgroundColor: '#CAD5E2',
                  }}
                />
              )}
              {canImportJson && !disabled && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={(): void => {
                    openJsonImportModal();
                  }}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: '16px', color: orange50 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: orange50,
                    }}
                  >
                    {/* #3039: 編集モードは既存設定の書き換えになるため文言を変える */}
                    {isEditMode ? 'JSONで編集' : 'JSONで取り込む'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* 項目設定の説明文 */}
      <Typography
        sx={{
          fontSize: '13px',
          color: gray80,
          textAlign: 'left',
          lineHeight: 1.6,
          marginBottom: '24px',
        }}
      >
        AIがファイルから自動で項目を読み取りました。項目名やデータ型を確認し、必要に応じて編集・追加・削除してください。
      </Typography>

      {/* タイトル行（親要素と同じ構造） */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
          padding: '0 12px',
          marginBottom: '8px',
        }}
      >
        {/* 項目名 */}
        <Box
          sx={{
            flex: 4,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Box sx={{ width: '26px' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...regularSm, color: gray80, textAlign: 'left' }}>項目名</Typography>
          </Box>
        </Box>
        {/* データの種類 */}
        <Box sx={{ flex: 3, minWidth: 0 }}>
          <Typography sx={{ ...regularSm, color: gray80, textAlign: 'left' }}>
            データの種類
          </Typography>
        </Box>
        {/* アイコングループ分のスペース */}
        <Box sx={{ width: '140px', flexShrink: 0 }} />
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Box>
          <SortableContext
            items={fields.map(field => field.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            {fields.map((item, index: number) => (
              <SortableItem
                key={item.id}
                id={item.id.toString()}
                index={index}
                control={control}
                errors={errors}
                fieldName={fieldName}
                disabled={disabled}
                validated={validated}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                onRemove={handleRemoveItem}
                onInsert={handleInsertItem}
                onAddChild={handleAddChild}
                existingItemIds={existingItemIds}
                onDeleteRequest={onDeleteRequest}
                onEditItem={handleEditItem}
                onRemoveChild={(parentIndex, childIndex): void => {
                  const current = watch(fieldName) as ItemListType[];
                  const parent = current[parentIndex];
                  const newChildren = parent.children.filter(
                    (_: ItemListChildType, i: number) => i !== childIndex,
                  );
                  const newList = current.map((item: ItemListType, idx: number) =>
                    idx === parentIndex
                      ? {
                          ...item,
                          type: newChildren.length === 0 ? 'string' : item.type,
                          children: newChildren,
                        }
                      : item,
                  );
                  // UUID方式: 子項目削除時はIDを変更しない
                  setValue(fieldName, newList);
                  if (validated) {
                    trigger(fieldName);
                  }
                }}
                onInsertChild={(parentIndex, childIndex): void => {
                  const current = watch(fieldName) as ItemListType[];
                  // 項目数上限チェック（OCR対象項目数で判定。追加項目はデフォルトOCR対象。#3045）
                  const ocrTargetCount = getOcrTargetItemCount(current);
                  if (ocrTargetCount >= getMaxItemCount()) {
                    onItemLimitExceeded?.();
                    return;
                  }
                  const parent = current[parentIndex];
                  const newChild = {
                    id: '0', // 保存時にnormalizeItemListIdsで適切なIDに再設定される
                    childValue: '',
                    type: 'string',
                    displayWidth: displayWidth.medium,
                    is_active: true, // デフォルトで有効
                    is_ocr_target: true, // デフォルトでOCR対象（#2968）
                  };
                  const newChildren = [
                    ...parent.children.slice(0, childIndex + 1),
                    newChild,
                    ...parent.children.slice(childIndex + 1),
                  ];
                  const newList = current.map((item: ItemListType, idx: number) =>
                    idx === parentIndex
                      ? {
                          ...item,
                          type: 'array',
                          children: newChildren,
                        }
                      : item,
                  );
                  // UUID方式: 追加時はIDを変更しない（保存時に採番）
                  setValue(fieldName, newList);
                  trigger(fieldName);
                  onItemInserted?.();
                }}
              />
            ))}
          </SortableContext>
        </Box>
      </DndContext>

      {/* 項目を追加ボタン（SPではPatternAdd側でカード外に配置） */}
      {!disabled && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '10px',
            marginBottom: '8px',
            height: '48px',
            border: `1px solid ${orange50}`,
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(240, 60, 10, 0.05)',
            },
            '@media screen and (max-width: 960px)': {
              display: 'none',
            },
          }}
          onClick={(): void => handleInsertItem()}
        >
          <Box
            sx={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '16px', color: orange50, fontWeight: 600 }}>＋</Typography>
          </Box>
          <Typography
            sx={{ marginLeft: '4px', fontSize: '16px', color: orange50, fontWeight: 600 }}
          >
            項目を追加
          </Typography>
        </Box>
      )}

      {/* 詳細設定モーダル */}
      <ItemEditModal
        open={editModalOpen}
        onClose={(): void => {
          setEditModalOpen(false);
          setEditModalData(null);
        }}
        onSave={handleEditModalSave}
        itemData={editModalData}
        saveButtonLabel="設定する"
        calculationContext={editModalCalcContext ?? undefined}
      />

      {/* JSON取り込みモーダル（運営スタッフのみ） */}
      <Dialog
        open={jsonImportOpen}
        onClose={closeJsonImportModal}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            padding: '24px',
            // 差分表示はJSONの行をそのまま並べるため、入力時より広い幅を確保する
            width: jsonPendingList ? '900px' : '700px',
            maxWidth: '95vw',
          },
        }}
      >
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: gray80, marginBottom: '8px' }}>
          {/* #3039: 編集モードでは既存設定の書き換えになるため文言を変える */}
          {isEditMode ? 'JSONで項目リストを編集する' : 'JSONで項目リストを取り込む'}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: gray80, marginBottom: '16px' }}>
          {isEditMode
            ? '現在の設定をJSONで表示しています。項目IDを変更すると、既存の読み取り結果との紐付けが失われます。'
            : '以下の形式のJSONを貼り付けてください。既存の項目リストは上書きされます。'}
        </Typography>
        {/* #3039: 差分プレビュー中は入力欄を隠す */}
        {jsonPendingList ? (
          <JsonImportDiffView
            beforeJson={jsonBeforeText}
            afterJson={jsonAfterText}
            diffEntries={jsonDiffEntries}
            mappingNotices={jsonMappingNotices}
          />
        ) : (
          <>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 700, color: gray80, marginBottom: '4px' }}
            >
              記入例
            </Typography>
            <Box
              sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '4px',
                padding: '8px 12px',
                marginBottom: '16px',
                fontSize: '12px',
                // gray60（#B3B3B3）は #F5F5F5 の背景に対してコントラスト比が約1.9:1しかなく
                // 読みづらいため gray80（約4.6:1）にする
                color: gray80,
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                overflowX: 'auto',
              }}
            >{`[
  { "id": "1", "name": "注文番号", "type": "string", "extraction_rule": "取得ルール（任意）" },
  { "id": "2", "name": "備考", "type": "string", "is_active": false },
  { "id": "3", "name": "区分", "type": "string", "is_ocr_target": false, "fixed_value": "0" },
  { "id": "4", "name": "税区分", "type": "select", "choices": [
    { "label": "課税", "value": "1" },
    { "label": "非課税", "value": "0" }
  ]},
  { "id": "5", "name": "注文情報", "type": "array", "children": [
    { "id": "5-1", "name": "商品名", "type": "string", "extraction_rule": "..." },
    { "id": "5-2", "name": "数量", "type": "number",
      "required": true, "treat_zero_as_empty": true },
    { "id": "5-3", "name": "単位", "type": "select", "choices": ["個", "箱"] },
    { "id": "5-4", "name": "単価", "type": "number", "field_code": "単価" },
    { "id": "5-5", "name": "小計", "type": "number",
      "is_calculated": true, "formula": "{単価} * {数量}" }
  ]}
]

※ "id" は既存項目のIDを維持するためのものです。
   省略すると新しい項目として追加されます。
※ "formula" の項目参照は "field_code" で書きます。
※ "treat_zero_as_empty" は 0 を空欄として扱う設定です（数字型のみ）。
   "required": true と併用したときだけ意味を持ちます。`}</Box>
            <TextField
              multiline
              rows={14}
              fullWidth
              placeholder={'[\n  { "name": "項目名", "type": "string" }\n]'}
              value={jsonInputText}
              onChange={(e): void => {
                setJsonInputText(e.target.value);
                setJsonImportError('');
              }}
              sx={{
                marginBottom: '8px',
                fontFamily: 'monospace',
                // 項目数が多いJSONも編集できるよう、入力欄を縦にドラッグで広げられるようにする
                '& .MuiInputBase-inputMultiline': {
                  resize: 'vertical',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: gray100,
                  borderWidth: '1px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: gray100,
                },
              }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '13px' } }}
            />
            {jsonImportError && (
              <Typography sx={{ fontSize: '12px', color: '#E53935', marginBottom: '8px' }}>
                {jsonImportError}
              </Typography>
            )}
          </>
        )}
        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Button
            // #3039: プレビュー中は入力画面へ戻る。入力中はモーダルを閉じる
            onClick={
              jsonPendingList
                ? (): void => {
                    setJsonPendingList(null);
                    setJsonDiffEntries([]);
                    setJsonMappingNotices([]);
                  }
                : closeJsonImportModal
            }
            sx={{
              height: '40px',
              padding: '0 20px',
              backgroundColor: '#F0F0F0',
              color: gray80,
              '&:hover': { backgroundColor: '#E0E0E0' },
            }}
          >
            {jsonPendingList ? '戻る' : 'キャンセル'}
          </Button>
          <Button
            // #3039: 入力中は差分確認へ進み、プレビュー中に反映を実行する
            onClick={jsonPendingList ? handleJsonImportApply : handleJsonImport}
            sx={{
              height: '40px',
              padding: '0 20px',
              backgroundColor: orange50,
              color: white,
              '&:hover': { backgroundColor: '#E55A2B' },
            }}
          >
            {jsonPendingList ? '反映する' : '差分を確認'}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ItemListManager;
